import { type ReactNode, Suspense } from 'react';
import { matchPath, Navigate, Outlet, type RouteObject, useLocation } from 'react-router';

import { AppChrome } from '@/App';
import { Loading } from '@/components/ui/feedback';
import { type Action, can } from '@/config/access';
import { CAMPAIGN_PATTERN, campaignSectionPattern, LOGIN_PATH } from '@/config/paths';
import {
  type AppRoute,
  BRAND_HUB_ROUTES,
  CAMPAIGN_SECTION_ROUTES,
  DEFAULT_ROUTE,
  type RouteHandle,
  UNLISTED_ROUTES,
  WORKSPACE_ROUTES,
} from '@/config/routes';
import { useActiveMembership, useSession } from '@/features/auth/useSession';
import { LoginPage } from '@/pages/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { RouteErrorPage } from '@/pages/RouteErrorPage';

/** Shows the page, or the not-found page when the membership may not open it. */
export function Guarded({ action, children }: { action?: Action; children: ReactNode }) {
  const membership = useActiveMembership();
  return can(membership, action) ? <>{children}</> : <NotFoundPage />;
}

/** Shows the sign-in page without a session, and the routes with one. */
export function RootLayout() {
  const session = useSession();
  const { pathname } = useLocation();

  if (!session) return <LoginPage />;
  if (matchPath(LOGIN_PATH, pathname)) return <Navigate to={DEFAULT_ROUTE} replace />;
  return <Outlet />;
}

/** Shows the shell, with the page in its content area. */
export function ShellLayout() {
  return (
    <AppChrome>
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    </AppChrome>
  );
}

/** Shows a route with no sidebar, no top bar and no page heading. */
export function BareLayout() {
  return (
    <Suspense fallback={<Loading className="h-screen" />}>
      <Outlet />
    </Suspense>
  );
}

const asChild = (route: AppRoute, path = route.path): RouteObject => ({
  path,
  element: <Guarded action={route.action}>{route.element}</Guarded>,
  handle: { route } satisfies RouteHandle,
});

const SHELL_ROUTES: AppRoute[] = [
  ...WORKSPACE_ROUTES,
  ...BRAND_HUB_ROUTES,
  ...UNLISTED_ROUTES.filter((route) => !route.chromeless),
];

const CAMPAIGN_SECTIONS: RouteObject[] = CAMPAIGN_SECTION_ROUTES.map((route) =>
  asChild(route, campaignSectionPattern(route.path)),
);

/** Every route of the app, as the data router takes them. */
export const ROUTE_TREE: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <BareLayout />,
        errorElement: <RouteErrorPage />,
        children: UNLISTED_ROUTES.filter((route) => route.chromeless).map((route) => asChild(route)),
      },
      {
        element: <ShellLayout />,
        children: [
          {
            errorElement: <RouteErrorPage />,
            children: [
              { path: '/', element: <Navigate to={DEFAULT_ROUTE} replace /> },
              ...SHELL_ROUTES.map((route) => asChild(route)),
              ...CAMPAIGN_SECTIONS,
              {
                path: CAMPAIGN_PATTERN,
                element: <Navigate to="brief" replace relative="path" />,
              },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
];
